import React, { useState, useEffect } from 'react';
import { BookOpen, Award, CheckCircle, X, Clock, Loader2, ChevronDown,
         AlertCircle, Save, Users, CalendarCheck, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';

const ATTENDANCE_OPTS = [
  { value: 'present', label: 'P', color: 'bg-green-500 text-white', border: 'border-green-500' },
  { value: 'absent',  label: 'A', color: 'bg-red-500 text-white',   border: 'border-red-500' },
  { value: 'late',    label: 'L', color: 'bg-amber-500 text-white', border: 'border-amber-500' },
];

export default function ClassTeacherPage() {
  const [tab, setTab] = useState('attendance');
  const [assignmentInfo, setAssignmentInfo] = useState(null);
  const [classInfo, setClassInfo] = useState(null);
  const [students, setStudents] = useState([]);
  const [terms, setTerms] = useState([]);
  const [selectedTermId, setSelectedTermId] = useState('');
  const [loading, setLoading] = useState(true);

  // Attendance state
  const [attDate, setAttDate] = useState(new Date().toISOString().slice(0, 10));
  const [attStatus, setAttStatus] = useState({});
  const [existingAtt, setExistingAtt] = useState({});
  const [savingAtt, setSavingAtt] = useState(false);

  // Comments state
  const [comments, setComments] = useState({});
  const [existingComments, setExistingComments] = useState({});
  const [saving, setSaving] = useState('');

  useEffect(() => { loadData(); }, []);
  useEffect(() => { if (classInfo) loadAttendance(); }, [attDate, classInfo]);
  useEffect(() => { if (classInfo && selectedTermId) loadComments(); }, [classInfo, selectedTermId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [myRes, sesRes] = await Promise.all([
        api.get('/academics/my-assignments'),
        api.get('/academics/current-session'),
      ]);
      setAssignmentInfo(myRes);
      const classTeacherFor = myRes.class_teacher_for;
      if (!classTeacherFor) { setLoading(false); return; }
      setClassInfo(classTeacherFor);

      const tList = sesRes.data?.terms_for_year || [];
      setTerms(tList);
      const currentTermId = sesRes.data?.term?.id || (tList[0]?.id || '');
      setSelectedTermId(currentTermId);

      const studRes = await api.get(`/students?class_id=${classTeacherFor.id}&status=active&limit=100`);
      setStudents(studRes.data || []);
    } catch { toast.error('Failed to load class info'); }
    finally { setLoading(false); }
  };

  const loadAttendance = async () => {
    if (!classInfo) return;
    try {
      const res = await api.get(`/academics/attendance?class_id=${classInfo.id}&date=${attDate}`);
      const map = {};
      (res.data || []).forEach(r => { map[r.student_id] = r.status; });
      setExistingAtt(map);
      setAttStatus({});
    } catch {}
  };

  const loadComments = async () => {
    if (!classInfo || !selectedTermId) return;
    try {
      const res = await api.get(`/academics/student-comments?class_id=${classInfo.id}&term_id=${selectedTermId}`);
      const map = {};
      (res.data || []).filter(c => c.comment_type === 'class_teacher').forEach(c => { map[c.student_id] = c.comment; });
      setExistingComments(map);
      setComments({});
    } catch {}
  };

  const saveAttendance = async () => {
    const merged = { ...existingAtt, ...attStatus };
    const records = students.map(s => ({
      student_id: s.id,
      status: merged[s.id] || 'absent',
    }));
    if (!records.length) { toast.error('No students'); return; }
    setSavingAtt(true);
    try {
      await api.post('/academics/attendance', {
        class_id: classInfo.id,
        class_name: classInfo.name,
        date: attDate,
        records,
      });
      toast.success('Attendance saved');
      setExistingAtt(merged);
      setAttStatus({});
    } catch (err) {
      toast.error(err?.detail || 'Failed to save attendance');
    } finally { setSavingAtt(false); }
  };

  const saveComment = async (studentId) => {
    const comment = comments[studentId] ?? existingComments[studentId] ?? '';
    if (!comment.trim()) { toast.error('Comment is empty'); return; }
    setSaving(studentId);
    try {
      await api.post('/academics/student-comments', {
        student_id: studentId, term_id: selectedTermId,
        comment: comment.trim(), comment_type: 'class_teacher',
      });
      toast.success('Comment saved');
      setExistingComments(p => ({ ...p, [studentId]: comment.trim() }));
      setComments(p => { const n = { ...p }; delete n[studentId]; return n; });
    } catch (err) { toast.error(err?.detail || 'Failed'); }
    finally { setSaving(''); }
  };

  const saveAllComments = async () => {
    const toSave = students.filter(s => comments[s.id]?.trim());
    if (!toSave.length) { toast.info('No new comments to save'); return; }
    setSaving('all');
    let saved = 0;
    for (const st of toSave) {
      try {
        await api.post('/academics/student-comments', {
          student_id: st.id, term_id: selectedTermId,
          comment: comments[st.id].trim(), comment_type: 'class_teacher',
        });
        setExistingComments(p => ({ ...p, [st.id]: comments[st.id].trim() }));
        saved++;
      } catch {}
    }
    setComments({});
    setSaving('');
    toast.success(`${saved} comment${saved !== 1 ? 's' : ''} saved`);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
    </div>
  );

  if (!classInfo) return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-8 text-center">
        <AlertCircle className="w-10 h-10 mx-auto mb-3 text-amber-400" />
        <h2 className="font-bold text-amber-800 text-lg mb-1">Not Assigned as Class Teacher</h2>
        <p className="text-sm text-amber-700">You are not currently assigned as a class teacher. Contact your school admin.</p>
      </div>
    </div>
  );

  const merged = { ...existingAtt, ...attStatus };
  const presentCount = students.filter(s => merged[s.id] === 'present').length;
  const absentCount = students.filter(s => merged[s.id] === 'absent').length;
  const pendingComments = Object.keys(comments).filter(k => comments[k]?.trim()).length;
  const savedComments = Object.keys(existingComments).length;

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="class-teacher-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <h1 className="text-xl font-bold text-slate-900">My Class</h1>
            <span className="bg-indigo-100 text-indigo-700 text-xs font-bold px-2.5 py-0.5 rounded-full">{classInfo.name}</span>
          </div>
          <p className="text-sm text-slate-500">{students.length} students enrolled</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 mb-5">
        {[
          { id: 'attendance', label: 'Attendance', icon: CalendarCheck },
          { id: 'comments', label: 'Term Comments', icon: MessageSquare },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors
              ${tab === t.id ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid={`tab-${t.id}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* Attendance Tab */}
      {tab === 'attendance' && (
        <div>
          <div className="flex items-center gap-4 mb-4 flex-wrap">
            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
              <CalendarCheck className="w-4 h-4 text-slate-400" />
              <input type="date" value={attDate} onChange={e => setAttDate(e.target.value)}
                className="text-sm outline-none bg-transparent" data-testid="attendance-date" />
            </div>
            <div className="flex gap-2 text-sm">
              <span className="bg-green-100 text-green-700 font-bold px-2.5 py-1 rounded-full">{presentCount} Present</span>
              <span className="bg-red-100 text-red-700 font-bold px-2.5 py-1 rounded-full">{absentCount} Absent</span>
            </div>
            <button onClick={saveAttendance} disabled={savingAtt}
              className="ml-auto flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-50 transition-colors"
              data-testid="save-attendance-btn">
              {savingAtt ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Attendance
            </button>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="grid grid-cols-12 bg-slate-50 border-b border-slate-200 px-4 py-2.5 text-xs font-bold text-slate-500 uppercase tracking-wide">
              <div className="col-span-1">#</div>
              <div className="col-span-6">Student</div>
              <div className="col-span-5 text-center">Status</div>
            </div>
            {students.length === 0 ? (
              <div className="py-10 text-center text-slate-400">No students enrolled</div>
            ) : students.map((st, i) => {
              const status = attStatus[st.id] || existingAtt[st.id] || '';
              return (
                <div key={st.id} className="grid grid-cols-12 items-center px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
                  <div className="col-span-1 text-sm text-slate-400 font-mono">{i + 1}</div>
                  <div className="col-span-6">
                    <p className="text-sm font-semibold text-slate-800">{st.first_name} {st.last_name}</p>
                    <p className="text-xs text-slate-400">{st.admission_number}</p>
                  </div>
                  <div className="col-span-5 flex justify-center gap-1.5">
                    {ATTENDANCE_OPTS.map(opt => (
                      <button key={opt.value}
                        onClick={() => setAttStatus(p => ({ ...p, [st.id]: opt.value }))}
                        className={`w-8 h-8 rounded-full text-xs font-extrabold border-2 transition-all
                          ${status === opt.value ? `${opt.color} ${opt.border} scale-110 shadow-sm` : 'bg-white text-slate-400 border-slate-200 hover:border-slate-400'}`}
                        data-testid={`att-${st.id}-${opt.value}`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Comments Tab */}
      {tab === 'comments' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="relative">
                <select value={selectedTermId} onChange={e => setSelectedTermId(e.target.value)}
                  className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white outline-none appearance-none pr-8"
                  data-testid="comment-term-select">
                  {terms.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
              </div>
              <span className="text-xs text-slate-400">{savedComments}/{students.length} comments written</span>
            </div>
            {pendingComments > 0 && (
              <button onClick={saveAllComments} disabled={saving === 'all'}
                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
                data-testid="save-all-comments-btn">
                {saving === 'all' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save All ({pendingComments})
              </button>
            )}
          </div>

          <div className="space-y-2.5">
            {students.map(student => {
              const existing = existingComments[student.id];
              const draft = comments[student.id];
              const display = draft !== undefined ? draft : (existing || '');
              const isDirty = draft !== undefined && draft !== existing;
              return (
                <div key={student.id} className={`bg-white rounded-xl border shadow-sm overflow-hidden ${isDirty ? 'border-indigo-300' : 'border-slate-200'}`}>
                  <div className="flex items-center gap-3 px-4 py-2.5 bg-slate-50 border-b border-slate-100">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-indigo-700">{student.first_name?.charAt(0)}</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-800">{student.first_name} {student.last_name}</p>
                    </div>
                    {existing && !isDirty && <span className="text-xs text-green-600 font-semibold flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5" /> Saved</span>}
                    {isDirty && <span className="text-xs text-amber-600 font-semibold">Unsaved</span>}
                  </div>
                  <div className="px-4 py-3 flex gap-3">
                    <textarea placeholder="Write class teacher's remark for report card..."
                      value={display}
                      onChange={e => setComments(p => ({ ...p, [student.id]: e.target.value }))}
                      className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2 outline-none resize-none h-14 focus:border-indigo-400"
                      data-testid={`comment-${student.id}`} />
                    <button onClick={() => saveComment(student.id)} disabled={saving === student.id || !display.trim()}
                      className="self-start flex items-center gap-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-3 py-2 rounded-lg disabled:opacity-40 transition-colors"
                      data-testid={`save-comment-${student.id}`}>
                      {saving === student.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
